import React, {useEffect, useState} from 'react'
import {ReportPageTitle} from './PageTitle/ReportPageTitle'
import {useAuth} from '../../../modules/auth'
import {collection, doc, onSnapshot} from 'firebase/firestore'
import db from '../../../../services/firebase'
import {ViewControl} from '../../controls/components/ViewControl'
import {Helmet} from 'react-helmet'
import {saveAs} from 'file-saver'
import ExcelJS from 'exceljs'
import useCrispChat from 'app/Hooks/useCrispChat'
import useAuditContext from 'app/Hooks/useAuditContext'
import {TestListLoading} from 'app/pages/tests/components/loading/TestListLoading'
import {ToastContainer} from 'react-bootstrap'
import UpgradePlan from 'app/components/UpgradePlan'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {processControlValues} from 'app/pages/controls/utils/controlUtils'
import Searchbar from 'app/components/Searchbar'
import OwnerDisplay from 'app/components/OwnerDisplay'
import Button from 'app/components/Button'
import ExportButton from 'app/components/ExportButton'

interface Control {
  id: string
  control_category: string
  control_description: string
  control_document: string
  control_evidence_status: string
  control_framework_mappings: string
  control_id: string
  control_owner: string
  control_test: string
  control_title: string
  control_uid: string
  document_id: string[]
  test_id: string[]
  deactivation_control: string
  category: string
  tag: string
}

interface GroupedControls {
  [chapter: string]: {
    [article: string]: Control[]
  }
}

const GDPR_CHAPTERS = [
  'Chapter 1: General Provisions',
  'Chapter 2: Principles',
  'Chapter 3: Rights of the Data Subject',
  'Chapter 4: Controller and Processor',
  'Chapter 5: Data Transfers',
  // 'Chapter 6: Independent Supervisory Authorities',
  // 'Chapter 7: Cooperation and Consistency',
  // 'Chapter 8: Remedies, Liability and Penalties',
] as const

const extractArticleTags = (mappings: string | undefined): string[] => {
  if (!mappings) return []
  return mappings
    .split(',')
    .map((mapping) => {
      const trimmed = mapping.trim()
      const articleMatch = trimmed.match(/GDPR ‧ Article (\d+)/i)
      if (articleMatch) {
        return `Article ${articleMatch[1]}`
      }
      return trimmed
    })
    .filter((tag) => tag.startsWith('Article '))
}

const getChapterFromArticle = (article: string): string => {
  const articleNumber = parseInt(article.replace('Article ', ''))

  if (articleNumber <= 4) return 'Chapter 1: General Provisions'
  if (articleNumber <= 11) return 'Chapter 2: Principles'
  if (articleNumber <= 23) return 'Chapter 3: Rights of the Data Subject'
  if (articleNumber <= 43) return 'Chapter 4: Controller and Processor'
  if (articleNumber <= 50) return 'Chapter 5: Data Transfers'
  // if (articleNumber <= 59) return 'Chapter 6: Independent Supervisory Authorities'
  // if (articleNumber <= 76) return 'Chapter 7: Cooperation and Consistency'
  // if (articleNumber <= 84) return 'Chapter 8: Remedies, Liability and Penalties'

  return 'Other'
}

const sortArticleTags = (a: string, b: string): number => {
  const aNum = parseInt(a.replace('Article ', ''))
  const bNum = parseInt(b.replace('Article ', ''))
  return aNum - bNum
}

const getUniqueCategories = () => {
  return ['all', ...GDPR_CHAPTERS]
}

const ViewFullScreenLink = ({
  isTrial,
  controlId,
  onTrialClick,
  onOpenModal,
}: {
  isTrial: boolean
  controlId: string
  onTrialClick: () => void
  onOpenModal: (id: string) => void
}) => {
  const {currentUser} = useAuth()
  const {currentAuditContext} = useAuditContext()
  
  const linkProps = isTrial
    ? {
        href: '#',
        rel: 'noopener noreferrer',
        onClick: (e: React.MouseEvent) => {
          e.preventDefault()
          onTrialClick()
        },
        target: undefined,
        className: 'fw-semibold',
      }
    : {
        href: (currentAuditContext || currentUser?.role === 'Auditor') ? '#' : `/controls/details/${controlId}`,
        rel: 'noopener noreferrer',
        target: '_blank',
        onClick: (e: React.MouseEvent) => {
          if (currentAuditContext || currentUser?.role === 'Auditor') {
            e.preventDefault()
            onOpenModal(controlId)
          }
        },
        style: {},
      }
  return (
    <a
      {...linkProps}
      className='d-flex fw-semibold align-items-center'
      aria-label='View in full Screen'
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke={isTrial ? '#5A5A5A' : 'currentColor'}
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='icon icon-tabler icons-tabler-outline icon-tabler-external-link mb-1 mx-1'
        aria-hidden='true'
      >
        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
        <path d='M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6' />
        <path d='M11 13l9 -9' />
        <path d='M15 4h5v5' />
      </svg>
      <span style={{color: '#5A5A5A'}}>
        {(currentAuditContext || currentUser?.role === 'Auditor') ? 'View details' : 'View in full Screen'}
      </span>
    </a>
  )
}

const GDPRReports = () => {
  const {currentUser} = useAuth()
  const [controls, setControls] = useState<GroupedControls>({})
  const [filteredControls, setFilteredControls] = useState<GroupedControls>({})
  const name = 'GDPR'
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [controlID, setControlID] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)
  const {toggleCrispChat} = useCrispChat()
  const [selectedChapter, setSelectedChapter] = useState<string>('all')
  const [showModal, setShowModal] = useState(false)
  const [tests, setTests] = useState([] as any[])

  useEffect(() => {
    getControl()
  }, [])

  const getControl = async () => {
    let unsubscribeTests: (() => void) | undefined
    try {
      if (!currentUser?.tenantId) {
        setLoading(false)
        return
      }

      const tenantRef = doc(db, 'tenants', currentUser.tenantId)
      const controlsCollectionRef = collection(tenantRef, 'controls')
      setShowEmptyState(true)
      const testsRef = collection(tenantRef, 'tests')

      unsubscribeTests = onSnapshot(testsRef, (snapshot) => {
        const testsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          status: doc.data().status,
        }))
        setTests(testsData)
      })

      const unsubscribe = onSnapshot(
        controlsCollectionRef,
        (snapshot) => {
          try {
            const controlsData = snapshot.docs.map((doc) => ({
              ...(doc.data() as Control),
              id: doc.id,
              control_owner: currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus' ? '' : doc.data().control_owner,
              control_title: doc.data().control_title || '',
            }))

            const filteredControls = controlsData.filter((control) =>
              control?.control_framework_mappings?.includes('GDPR')
            )

            const initialGrouping: GroupedControls = GDPR_CHAPTERS.reduce((acc, chapter) => {
              acc[chapter] = {}
              return acc
            }, {} as GroupedControls)

            const groupedControls = filteredControls.reduce((acc, control) => {
              if (!control.control_framework_mappings) return acc

              const articleTags = extractArticleTags(control.control_framework_mappings)

              articleTags.forEach((articleTag) => {
                if (!articleTag) return

                const chapter = getChapterFromArticle(articleTag)
                if (!acc[chapter]) {
                  acc[chapter] = {}
                }
                if (!acc[chapter][articleTag]) {
                  acc[chapter][articleTag] = []
                }
                if (!acc[chapter][articleTag].some((c) => c.id === control.id)) {
                  acc[chapter][articleTag].push(control)
                }
              })

              return acc
            }, initialGrouping)

            const orderedControls = GDPR_CHAPTERS.reduce((acc, chapter) => {
              if (groupedControls[chapter]) {
                acc[chapter] = Object.entries(groupedControls[chapter])
                  .sort(([a], [b]) => sortArticleTags(a, b))
                  .reduce(
                    (obj, [key, value]) => ({
                      ...obj,
                      [key]: value,
                    }),
                    {}
                  )
              }
              return acc
            }, {} as GroupedControls)

            if (currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') {
              const firstChapter = GDPR_CHAPTERS[0]
              const firstChapterControls: Control[] = []

              if (orderedControls[firstChapter]) {
                Object.values(orderedControls[firstChapter]).forEach((controls) => {
                  firstChapterControls.push(...controls)
                })
              }

              const unmaskedControlIds = new Set(
                firstChapterControls.slice(0, 6).map((control) => control.id)
              )

              Object.keys(orderedControls).forEach((chapter) => {
                Object.keys(orderedControls[chapter]).forEach((ccTag) => {
                  orderedControls[chapter][ccTag] = orderedControls[chapter][ccTag].map(
                    (control) => ({
                      ...control,
                      control_title: unmaskedControlIds.has(control.id)
                        ? control.control_title
                        : maskStringPreservingSpaces(control.control_title),
                      control_owner: unmaskedControlIds.has(control.id)
                        ? control.control_owner
                        : '',
                      control_description: unmaskedControlIds.has(control.id)
                        ? control.control_description
                        : maskStringPreservingSpaces(control.control_description),
                    })
                  )
                })
              })
            }
            setFilteredControls(orderedControls)
            setControls(orderedControls)
            setShowEmptyState(false)
            setLoading(false)
          } catch (error) {
            showErrorToast({message: 'Error processing data'})
            setShowEmptyState(false)
            setLoading(false)
          }
        },
        (error) => {
          showErrorToast({message: 'Error fetching data'})
          setShowEmptyState(false)
          setLoading(false)
        }
      )

      return () => unsubscribe()
    } catch (error) {
      showErrorToast({message: 'Failed to fetch controls'})
      setShowEmptyState(false)
      setLoading(false)
    }
  }

  const maskStringPreservingSpaces = (str, visibleStartLength = 3, visibleEndLength = 2) => {
    if (!str || str.length <= visibleStartLength + visibleEndLength) return '****'
    const start = str.slice(0, visibleStartLength)
    const end = str.slice(-visibleEndLength)
    const middle = str.slice(visibleStartLength, -visibleEndLength)
    const maskedMiddle = [...middle].map((char) => (char === ' ' ? ' ' : '*')).join('')
    return `${start}${maskedMiddle}${end}`
  }

  useEffect(() => {
    let unsubscribe: (() => void) | undefined

    const fetchData = async () => {
      unsubscribe = await getControl()
    }

    fetchData()

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [currentUser?.tenantId])

  const handleOpenModal = (id) => {
    setIsModalOpen(true)
    if (!id) {
      return
    }
    setControlID(id)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  let controlCounter = 0

  const shouldApplyBlur = (currentCount) => {
    if (currentUser?.account_type !== 'Trial' && currentUser?.account_type !== 'Trial-plus') return false
    return currentCount >= 6
  }

  const stripHtmlTags = (html) => {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  const exportData = async () => {
    if (Object.keys(filteredControls).length > 0) {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Controls')

      // Define header row
      worksheet.columns = [
        {header: 'Control', key: 'controlObjective', width: 30},
        {header: 'Control Description', key: 'controlDescription', width: 50},
        {header: 'Category', key: 'clause', width: 40},
        {header: 'Chapter', key: 'chapter', width: 40},
        {header: 'Framework', key: 'sec', width: 40},
        {header: 'Owner', key: 'owner', width: 20},
        {header: 'Platform', key: 'platform', width: 15},
        {header: 'Document', key: 'document', width: 15},
        {header: 'Status', key: 'status', width: 15},
        {header: 'Justification for exclusion', key: 'justification', width: 40},
      ]

      const headerRow = worksheet.getRow(1)
      headerRow.font = {bold: true, color: {argb: 'FFFFFFFF'}, size: 10}
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {argb: 'FF000000'},
      }
      headerRow.height = 30
      headerRow.alignment = {horizontal: 'center', vertical: 'middle'}
      worksheet.views = [{state: 'frozen', ySplit: 1}]

      const allControls: Control[] = []
      Object.values(filteredControls).forEach((chapterData) => {
        Object.values(chapterData).forEach((articleControls) => {
          articleControls.forEach((control) => {
            allControls.push(control)
          })
        })
      })

      const exportData = allControls
        .sort((a, b) => (a.control_title || '').localeCompare(b.control_title || ''))
        .map((row) => ({
          controlObjective: row.control_title || '',
          controlDescription: stripHtmlTags(row.control_description) || '',
          clause: row.category,
          chapter: row.tag,
          sec: row.control_framework_mappings || '',
          owner: row.control_owner || '',
          platform: row.control_test || '',
          document: row.control_document || '',
          status: row.control_evidence_status || '',
          justification: row.deactivation_control || '',
        }))

      exportData.forEach((item) => {
        const row = worksheet.addRow(item)
        row.height = 20
        row.font = {size: 10}

        if (item.status === 'Deactivated') {
          row.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: {argb: 'DDEEFF'},
            }
          })
        }

        if (item.status === 'OK') {
          row.getCell('G').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {argb: 'D9EAD3'},
          }
        } else if (item.status === 'Unhealthy') {
          row.getCell('G').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {argb: 'F4CCCC'},
          }
        }
      })

      const buffer = await workbook.xlsx.writeBuffer()
      const excelBlob = new Blob([buffer], {type: 'application/octet-stream'})

      saveAs(excelBlob, `controls_${name}.xlsx`)
      showSuccessToast({message: 'Controls data exported successfully'})
    }
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | GDPR Framework</title>
      </Helmet>
      <ReportPageTitle
        icon='GDPR.svg'
        title='GDPR'
        desc='Your progress towards GDPR compliance based on your controls and tests.'
      />
      {(currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') && (
       <div className='card' style={{ backgroundColor: '#F3FAF7', border: '1.5px solid #0E9F6E' }}>
       <div className='p-6 fs-6'>
         <div className='ms-4 fw-semibold d-flex align-items-center justify-content-between'>
           <div>
             <h5 className='fw-bold text-dark mb-2'>Support Your GDPR Compliance</h5>
             <p className='mb-0 text-gray-700 fs-7'>
               Accessible with any paid plan, this section highlights key data protection controls and privacy measures aligned with GDPR requirements.
             </p>
           </div>
           <Button btnType='primary' onClick={toggleCrispChat}>
             Contact sales
           </Button>
         </div>
       </div>
     </div>
      )}
      <div>
        <div className='card-header border-0 pt-6 h-100 mb-8'>
          <div className='d-flex justify-content-between align-items-center w-100'>
            {currentUser?.account_type !== 'Trial' && currentUser?.account_type !== 'Trial-plus' && (
              <>
                <div className='flex-lg-fill'>
                  <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                </div>

                <div className='d-flex'>
                  {currentUser?.account_type !== 'Trial' && currentUser?.account_type !== 'Trial-plus' && (
                    <div className='d-flex align-items-center mx-4'>
                      <select
                        className='form-select form-select-sm'
                        value={selectedChapter}
                        onChange={(e) => setSelectedChapter(e.target.value)}
                        style={{
                          minWidth: '200px',
                          height: '40px',
                          borderRadius: '6px',
                        }}
                      >
                        {getUniqueCategories().map((category) => (
                          <option key={category} value={category}>
                            {category === 'all' ? 'All Categories' : category}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <a
                    href='/company-settings/cookie-widget'
                    className=' btn btn-sm btn-light me-4 fs-6 d-flex align-items-center text-gray-900 fw-medium'
                    style={{
                      height: '40px',
                      background: '#fff',
                      color: '#000',
                      border: '1px solid #C5C5C5',
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='#111827'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      className='icon icon-tabler icons-tabler-outline icon-tabler-palette me-2'
                    >
                      <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                      <path d='M12 21a9 9 0 0 1 0 -18c4.97 0 9 3.582 9 8c0 1.06 -.474 2.078 -1.318 2.828c-.844 .75 -1.989 1.172 -3.182 1.172h-2.5a2 2 0 0 0 -1 3.75a1.3 1.3 0 0 1 -1 2.25' />
                      <path d='M8.5 10.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0' />
                      <path d='M12.5 7.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0' />
                      <path d='M16.5 10.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0' />
                    </svg>
                    Cookie Widget
                  </a>
                  {currentUser?.role !== 'Auditor' && (
                  <ExportButton
                    type='button'
                    onClick={exportData}
                  >
                    Export
                  </ExportButton>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <ViewControl isOpen={isModalOpen} onClose={handleCloseModal} id={controlID} />

        <div className='space-y-8'>
          {loading ? (
            <div className='d-flex justify-content-center align-items-center text-center' style={{height: '100vh', width: '100vw', position: 'absolute', top: '20vh', left: '20vh'}}>
              <TestListLoading />
            </div>
          ) : Object.keys(controls) && Object.keys(controls).length > 0 ? (
            Object.keys(controls)
              .filter((chapter) => selectedChapter === 'all' || chapter === selectedChapter)
              .map((category) => (
                <div key={category} className='rounded-lg overflow-hidden'>
                  <h2 className='text-xl font-bold pt-4'>{category}</h2>

                  <div className='space-y-6 mt-4 ml-4'>
                    {Object.keys(controls[category])
                      .sort(sortArticleTags)
                      .map((ccTag) => (
                        <div key={ccTag} className='card mb-4 w-full'>
                          <p className='card-header d-flex align-items-center fs-3 fw-semibold pe-9'>
                            {ccTag}
                          </p>
                          <div className='card-body pt-0'>
                            <div className='table-responsive'>
                              <table className='table'>
                                <thead className='fs-7 fw-bold' style={{color: '#5A5A5A'}}>
                                  <tr>
                                    <th className='min-w-500px py-6'>CONTROL TITLE</th>
                                    <th className='min-w-100px py-6'>STATUS</th>
                                    <th className='min-w-100px py-6'>TEST PROGRESS</th>
                                    <th className='min-w-100px py-6'>OWNER</th>
                                    <th className='min-w-100px py-6'>ACTION</th>
                                  </tr>
                                </thead>

                                <tbody className='bg-white divide-y fs-6'>
                                  {controls[category][ccTag]
                                    .sort((a, b) => (a.control_title || '').localeCompare(b.control_title || ''))
                                    .map((control) => {
                                    const currentControlIndex = controlCounter++

                                    return (
                                      <tr
                                        key={control.id}
                                        style={
                                          shouldApplyBlur(currentControlIndex)
                                            ? {filter: 'blur(4px)', pointerEvents: 'none'}
                                            : {}
                                        }
                                      >
                                        <td className='py-4 whitespace-nowrap'>
                                          <div className='text-sm text-gray-900'>
                                            <span
                                              className='text-dark fw-semibold text-hover-primary fs-6 cursor-pointer'
                                              onClick={e => {
                                                e.preventDefault();
                                                currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus'
                                                  ? setShowModal(true)
                                                  : handleOpenModal(control.id);
                                              }}
                                            >
                                              {control.control_title}
                                            </span>
                                          </div>
                                        </td>

                                        <td className='py-4 whitespace-nowrap'>
                                          {control.control_evidence_status === 'Deactivated' ? (
                                            <span
                                              className='badge fs-7 text-dark text-semibold m-1'
                                              style={{background: '#F6F6F6'}}
                                            >
                                              {control.control_evidence_status}
                                            </span>
                                          ) : (
                                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                            <span
                                              className={`badge fw-semibold fs-7 ${
                                                control.control_evidence_status === 'Ok' ||
                                                control.control_evidence_status === 'OK'
                                                  ? 'badge-primary'
                                                  : 'badge-danger'
                                              }`}
                                              style={{
                                                color:
                                                  control.control_evidence_status === 'Ok' ||
                                                  control.control_evidence_status === 'OK'
                                                    ? '#0E9F6E'
                                                    : '#EE3043',
                                                background:
                                                  control.control_evidence_status === 'Ok' ||
                                                  control.control_evidence_status === 'OK'
                                                    ? '#E8F8F4'
                                                    : '#FFF5F8',
                                              }}
                                            >
                                              {control.control_evidence_status === 'Ok' ||
                                              control.control_evidence_status === 'OK'
                                                ? 'OK'
                                                : 'Unhealthy'}
                                            </span>
                                          )}
                                        </td>
                                        <td className='py-4 whitespace-nowrap'>
                                          {currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus'
                                            ? '0/0'
                                            : processControlValues(
                                                control.control_test,
                                                control.control_document,
                                                control.test_id,
                                                control.document_id,
                                                tests
                                              )}{' '}
                                          Test passing
                                        </td>
                                        <td className='py-4 whitespace-nowrap'>
                                          {control.control_owner ? (
                                            <OwnerDisplay owner={control.control_owner} uid={control.control_uid} />
                                          ) : (
                                            <span>--</span>
                                          )}
                                        </td>
                                        <td className='pt-6 pb-6'>
                                          <ViewFullScreenLink
                                            isTrial={currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus'}
                                            controlId={control.id}
                                            onTrialClick={() => setShowModal(true)}
                                            onOpenModal={handleOpenModal}
                                          />
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))
          ) : (
            <>
              {!showEmptyState ? (
                <div className='text-center'>
                  <div className='m-20'>
                    <img src='/media/buttons/emptystate.svg' alt='' />
                    <div className='m-5 fs-5'>
                      <p style={{fontWeight: 'bold'}}>No controls found</p>
                    </div>
                    <p className=''>
                      We couldn't find any controls that match your search. Maybe try a different
                      search!
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  className='d-flex justify-content-center align-items-center text-center'
                  style={{
                    height: '100vh',
                    width: 'calc(100% - 40vh)',
                    position: 'absolute',
                    top: '20vh',
                    left: '40vh',
                  }}
                >
                  <TestListLoading />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ToastContainer />
      <UpgradePlan show={showModal} onClose={() => setShowModal(false)} />
    </>
  )
}

export default GDPRReports